import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface VisualBreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

const VisualBreadcrumbs: React.FC<VisualBreadcrumbsProps> = ({ items, className = "" }) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(segment => segment);
    const breadcrumbs: BreadcrumbItem[] = [{ name: 'Home', path: '/' }];
    
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Convert segment to readable name
      let name = segment.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
      // Special cases for better naming
      if (segment === 'tours') name = 'Tours';
      if (segment === 'financial-district') name = 'Financial District Tour';
      if (segment === 'brooklyn-heights') name = 'Brooklyn Heights Tour';
      if (segment === 'lower-east-side') name = 'Lower East Side Tour';
      if (segment === 'private-tours') name = 'Private Tours';
      if (segment === 'press-kit') name = 'Press Kit';
      if (segment === 'in-the-news') name = 'In the News';
      if (segment === 'petition-jerry-fox') name = 'Jerry Fox Petition';
      if (segment === 'about') name = 'About Us';
      if (segment === 'contact') name = 'Contact Us';
      
      breadcrumbs.push({ name, path: currentPath });
    });
    
    return breadcrumbs;
  };

  const breadcrumbItems = items || generateBreadcrumbs();
  
  // Don't show breadcrumbs on homepage
  if (location.pathname === '/') {
    return null;
  }

  return (
    <nav aria-label="Breadcrumb" className={`py-4 ${className}`}>
      <div className="container mx-auto px-4">
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            const isHome = index === 0;
            
            return (
              <li key={item.path} className="flex items-center">
                {index > 0 && (
                  <ChevronRight size={16} className="text-neutral-gray-400 mx-2" />
                )}
                
                {isLast ? (
                  <span className="text-neutral-gray-600 font-medium" aria-current="page">
                    {isHome && <Home size={16} className="inline mr-1" />}
                    {item.name}
                  </span>
                ) : (
                  <Link 
                    to={item.path} 
                    className="text-accent hover:text-primary transition-colors duration-200 flex items-center"
                  >
                    {isHome && <Home size={16} className="inline mr-1" />}
                    {item.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
};

export default VisualBreadcrumbs;